import { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react'
import { doc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from './AuthContext'

const SettingsContext = createContext(null)

const DEFAULT_SETTINGS = {
  profile: {
    age: '',
    income: '',
    filingStatus: '',
    dependents: '',
    state: '',
    goals: '',
  },
  calculatorUsage: {},
  calculatorDefaults: {
    investment: {
      initialAmount: 10000,
      monthlyContribution: 500,
      annualReturn: 7,
      years: 30,
    },
    retirement: {
      currentAge: 35,
      retirementAge: 60,
      currentSalary: 55000,
      salaryGrowth: 2.5,
      yearsOfService: 5,
      planPreset: 'strs_ohio',
    },
    loan: {
      principal: 350000,
      downPayment: 70000,
    },
    rothVsTraditional: {
      filingStatus: 'single',
      grossIncome: 85000,
      currentAge: 30,
      retirementAge: 65,
      annualContribution: 23500,
      expectedReturn: 7,
      selectedState: 'OH',
    },
  },
}

function deepMerge(defaults, saved) {
  if (!saved) return { ...defaults }
  const result = { ...defaults }
  for (const key of Object.keys(defaults)) {
    if (saved[key] === undefined) continue
    if (
      typeof defaults[key] === 'object' &&
      defaults[key] !== null &&
      !Array.isArray(defaults[key])
    ) {
      result[key] = deepMerge(defaults[key], saved[key])
    } else {
      result[key] = saved[key]
    }
  }
  return result
}

function getStorageKey(uid) {
  return `liquid-finance-settings-${uid}`
}

function loadSettingsFromLocal(uid) {
  if (!uid) return { ...DEFAULT_SETTINGS }
  try {
    const raw = localStorage.getItem(getStorageKey(uid))
    if (!raw) return { ...DEFAULT_SETTINGS }
    const saved = JSON.parse(raw)
    return deepMerge(DEFAULT_SETTINGS, saved)
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

function saveSettingsToLocal(uid, settings) {
  if (!uid) return
  try {
    localStorage.setItem(getStorageKey(uid), JSON.stringify(settings))
  } catch {
    // localStorage full or unavailable
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD':
      return action.payload
    case 'SET_PROFILE':
      return { ...state, profile: { ...state.profile, ...action.payload } }
    case 'SET_CALCULATOR_DEFAULTS':
      return {
        ...state,
        calculatorDefaults: {
          ...state.calculatorDefaults,
          [action.payload.calculator]: {
            ...state.calculatorDefaults[action.payload.calculator],
            ...action.payload.values,
          },
        },
      }
    case 'RESET_CALCULATOR_DEFAULTS':
      return {
        ...state,
        calculatorDefaults: {
          ...state.calculatorDefaults,
          [action.payload]: DEFAULT_SETTINGS.calculatorDefaults[action.payload],
        },
      }
    case 'INCREMENT_CALCULATOR_USE':
      return {
        ...state,
        calculatorUsage: {
          ...state.calculatorUsage,
          [action.payload]: (state.calculatorUsage?.[action.payload] || 0) + 1,
        },
      }
    case 'RESET_ALL':
      return { ...DEFAULT_SETTINGS }
    default:
      return state
  }
}

export function SettingsProvider({ children }) {
  const { user } = useAuth()
  const [settings, dispatch] = useReducer(reducer, DEFAULT_SETTINGS)
  const firestoreLoaded = useRef(false)
  const isLocalDispatch = useRef(false)

  // Subscribe to Firestore and load settings when user changes
  useEffect(() => {
    firestoreLoaded.current = false
    isLocalDispatch.current = false

    if (!user?.uid) {
      dispatch({ type: 'LOAD', payload: { ...DEFAULT_SETTINGS } })
      return
    }

    // Load from localStorage immediately for fast display
    const localSettings = loadSettingsFromLocal(user.uid)
    dispatch({ type: 'LOAD', payload: localSettings })

    // Subscribe to Firestore for real-time sync
    const docRef = doc(db, 'userSettings', user.uid)
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const firestoreData = snapshot.data()
          const merged = deepMerge(DEFAULT_SETTINGS, firestoreData)
          firestoreLoaded.current = true
          isLocalDispatch.current = false
          dispatch({ type: 'LOAD', payload: merged })
          saveSettingsToLocal(user.uid, merged)
        } else {
          // No Firestore doc yet — migrate local data
          firestoreLoaded.current = true
          const localData = loadSettingsFromLocal(user.uid)
          setDoc(docRef, localData).catch(() => {})
        }
      },
      () => {
        // Firestore error — fall back to local only
        firestoreLoaded.current = true
      },
    )

    return unsubscribe
  }, [user?.uid])

  // Save on every user-initiated change (not from Firestore snapshot)
  useEffect(() => {
    if (!user?.uid || !firestoreLoaded.current) return
    if (!isLocalDispatch.current) {
      // This change came from Firestore snapshot, skip writing back
      isLocalDispatch.current = true
      return
    }

    saveSettingsToLocal(user.uid, settings)
    const docRef = doc(db, 'userSettings', user.uid)
    setDoc(docRef, settings).catch(() => {})
  }, [settings, user?.uid])

  const updateProfile = useCallback((fields) => {
    isLocalDispatch.current = true
    dispatch({ type: 'SET_PROFILE', payload: fields })
  }, [])

  const setCalculatorDefaults = useCallback((calculator, values) => {
    isLocalDispatch.current = true
    dispatch({ type: 'SET_CALCULATOR_DEFAULTS', payload: { calculator, values } })
  }, [])

  const resetCalculatorDefaults = useCallback((calculator) => {
    isLocalDispatch.current = true
    dispatch({ type: 'RESET_CALCULATOR_DEFAULTS', payload: calculator })
  }, [])

  const resetAll = useCallback(() => {
    isLocalDispatch.current = true
    dispatch({ type: 'RESET_ALL' })
  }, [])

  const trackCalculatorOpen = useCallback((key) => {
    isLocalDispatch.current = true
    dispatch({ type: 'INCREMENT_CALCULATOR_USE', payload: key })
  }, [])

  return (
    <SettingsContext.Provider value={{
      settings,
      updateProfile,
      setCalculatorDefaults,
      resetCalculatorDefaults,
      resetAll,
      trackCalculatorOpen,
      DEFAULT_SETTINGS,
    }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider')
  return ctx
}
